/**
 * StorageService — orchestrates the full storage pipeline.
 *
 * Pipeline: validate file → process image (if applicable) →
 * generate path → upload via provider → generate thumbnail/
 * responsive variants → return StoredFile.
 *
 * Integrates with: Logger, Analytics, Audit, Performance.
 * Provider-agnostic: swaps between Local (dev) and Supabase (prod).
 */

import type {
  StorageProvider,
  StorageBucket,
  StorageFileCategory,
  StoredFile,
  UploadOptions,
  FileMetadata,
  SignedUrlOptions,
  ListOptions,
  ListResult,
} from './types';
import { LocalStorageProvider } from './LocalStorageProvider';
import { SupabaseStorageProvider } from './SupabaseStorageProvider';
import { validateFile, isImage, getExtension } from './validation';
import {
  UploadFailedError,
  UploadCancelledError,
  StorageProviderUnavailableError,
} from './errors';
import {
  compressImage,
  generateThumbnail,
  generateResponsiveImages,
  cropAvatar,
  getImageDimensions,
  supportsWebP,
} from './imageProcessing';
import { env } from '@/lib/env';
import { LoggerFactory } from '@/lib/logging';
import { analyticsService } from '@/lib/AnalyticsService';
import { auditService } from '@/lib/AuditService';
import { performanceService } from '@/lib/PerformanceService';
import { globalErrorService } from '@/lib/GlobalErrorService';

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `file-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildPath(userId: string, category: StorageFileCategory, filename: string): string {
  const id = generateId();
  const ext = getExtension(filename);
  const base = filename.includes('.') ? filename.slice(0, filename.lastIndexOf('.')) : filename;
  return `${userId}/${category}-${base}-${id}${ext ? '.' + ext : ''}`;
}

class StorageServiceImpl {
  private provider: StorageProvider;
  private logger = LoggerFactory.create();

  constructor(provider?: StorageProvider) {
    this.provider = provider ?? (env.isProduction
      ? SupabaseStorageProvider
      : LocalStorageProvider);
  }

  setProvider(provider: StorageProvider): void {
    this.provider = provider;
  }

  getProvider(): StorageProvider {
    return this.provider;
  }

  async upload(file: File, userId: string, options: UploadOptions): Promise<StoredFile> {
    analyticsService.track('upload_started', {
      bucket: options.bucket,
      category: options.category,
      fileSize: file.size,
      fileName: file.name,
    });

    const validation = validateFile(file, options.category, {
      maxFileSizeMB: options.maxFileSizeMB,
      allowedMimeTypes: options.allowedMimeTypes,
      allowedExtensions: options.allowedExtensions,
    });

    if (!validation.valid || !validation.sanitizedFilename) {
      throw new UploadFailedError(validation.error ?? 'File validation failed.');
    }

    if (options.signal?.aborted) throw new UploadCancelledError();

    const available = await this.provider.isAvailable();
    if (!available) throw new StorageProviderUnavailableError(this.provider.name);

    const sanitized = validation.sanitizedFilename;
    const path = buildPath(userId, options.category, sanitized);
    const useWebP = options.convertToWebP && supportsWebP() && isImage(file.type);
    const format = useWebP ? 'image/webp' : 'image/jpeg';

    let body: Blob = file;
    let width: number | undefined;
    let height: number | undefined;
    let thumbnailUrl: string | undefined;
    let responsiveUrls: StoredFile['responsiveUrls'];

    return performanceService.measure(
      'storage.upload',
      async () => {
        try {
          if (isImage(file.type)) {
            const dims = await getImageDimensions(file);
            width = dims.width;
            height = dims.height;

            if (options.category === 'avatar') {
              const cropped = await cropAvatar(file, 256, format, 0.9);
              body = cropped.blob;
              width = cropped.width;
              height = cropped.height;
            } else if (options.maxImageWidth || options.maxImageHeight) {
              const maxDim = Math.max(options.maxImageWidth ?? 0, options.maxImageHeight ?? 0);
              if (maxDim > 0) {
                const compressed = await compressImage(file, maxDim, 0.85, format);
                body = compressed.blob;
                width = compressed.width;
                height = compressed.height;
              }
            }

            if (options.generateThumbnail) {
              const thumb = await generateThumbnail(body, 160, format, 0.8);
              const thumbPath = `${userId}/thumbnails/${path.split('/').pop()}`;
              const thumbResult = await this.provider.upload(
                options.bucket,
                thumbPath,
                thumb.blob,
                format,
                { upsert: false, onProgress: options.onProgress, signal: options.signal },
              );
              thumbnailUrl = thumbResult.url;
            }

            if (options.generateResponsive) {
              const responsive = await generateResponsiveImages(body, format, 0.85);
              const baseName = path.split('/').pop();
              const baseNoExt = baseName?.includes('.') ? baseName.slice(0, baseName.lastIndexOf('.')) : baseName;

              const [small, medium, large] = await Promise.all([
                this.provider.upload(options.bucket, `${userId}/responsive/small/${baseNoExt}.webp`, responsive.blobs.small, format),
                this.provider.upload(options.bucket, `${userId}/responsive/medium/${baseNoExt}.webp`, responsive.blobs.medium, format),
                this.provider.upload(options.bucket, `${userId}/responsive/large/${baseNoExt}.webp`, responsive.blobs.large, format),
              ]);

              responsiveUrls = { small: small.url, medium: medium.url, large: large.url };
            }
          }

          const finalPath = useWebP ? path.replace(/\.(jpg|jpeg|png)$/i, '.webp') : path;
          const result = await this.provider.upload(options.bucket, finalPath, body, isImage(file.type) ? format : file.type, {
            upsert: options.upsert,
            onProgress: options.onProgress,
            signal: options.signal,
          });

          const storedFile: StoredFile = {
            id: generateId(),
            bucket: options.bucket,
            path: result.path,
            name: result.path.split('/').pop() ?? sanitized,
            originalName: file.name,
            mimeType: isImage(file.type) ? format : file.type,
            size: body.size,
            url: result.url,
            thumbnailUrl,
            responsiveUrls,
            width,
            height,
            uploadedAt: new Date().toISOString(),
            accessLevel: options.accessLevel ?? 'public',
            category: options.category,
          };

          analyticsService.track('upload_completed', {
            bucket: options.bucket,
            category: options.category,
            fileSize: body.size,
            path: result.path,
          });

          await auditService.record({
            actor_id: userId,
            actor_role: 'creator',
            target_type: 'file',
            target_id: result.path,
            action: 'profile_update',
            previous_value: null,
            new_value: { bucket: options.bucket, path: result.path, size: body.size },
            notes: `Uploaded ${options.category} file`,
          });

          return storedFile;
        } catch (err) {
          if (err instanceof UploadCancelledError) {
            analyticsService.track('upload_cancelled', {
              bucket: options.bucket,
              category: options.category,
            });
            throw err;
          }

          const errorMsg = err instanceof Error ? err.message : String(err);
          analyticsService.track('upload_failed', {
            bucket: options.bucket,
            category: options.category,
            error: errorMsg,
          });
          globalErrorService.capture(err, 'service', { operation: 'storage.upload', bucket: options.bucket });
          throw err instanceof Error ? err : new UploadFailedError(errorMsg);
        }
      },
      { bucket: options.bucket, category: options.category, fileSize: file.size },
    );
  }

  async downloadUrl(
    bucket: StorageBucket,
    path: string,
    options?: SignedUrlOptions,
  ): Promise<string> {
    return performanceService.measure(
      'storage.downloadUrl',
      () => this.provider.downloadUrl(bucket, path, options),
      { bucket, path },
    );
  }

  async delete(bucket: StorageBucket, path: string, userId: string): Promise<void> {
    return performanceService.measure(
      'storage.delete',
      async () => {
        await this.provider.delete(bucket, path);
        analyticsService.track('file_download', { bucket, path, action: 'delete' });
        await auditService.record({
          actor_id: userId,
          actor_role: 'creator',
          target_type: 'file',
          target_id: path,
          action: 'status_change',
          previous_value: { bucket, path },
          new_value: null,
          notes: `Deleted file from ${bucket}`,
        });
      },
      { bucket, path },
    );
  }

  async move(
    from: { bucket: StorageBucket; path: string },
    to: { bucket: StorageBucket; path: string },
    userId: string,
  ): Promise<void> {
    return performanceService.measure(
      'storage.move',
      async () => {
        await this.provider.move(from, to);
        await auditService.record({
          actor_id: userId,
          actor_role: 'creator',
          target_type: 'file',
          target_id: to.path,
          action: 'profile_update',
          previous_value: from,
          new_value: to,
          notes: `Moved file from ${from.bucket}/${from.path} to ${to.bucket}/${to.path}`,
        });
      },
      { from, to },
    );
  }

  async copy(
    from: { bucket: StorageBucket; path: string },
    to: { bucket: StorageBucket; path: string },
    userId: string,
  ): Promise<void> {
    return performanceService.measure(
      'storage.copy',
      async () => {
        await this.provider.copy(from, to);
        await auditService.record({
          actor_id: userId,
          actor_role: 'creator',
          target_type: 'file',
          target_id: to.path,
          action: 'profile_update',
          previous_value: from,
          new_value: to,
          notes: `Copied file from ${from.bucket}/${from.path} to ${to.bucket}/${to.path}`,
        });
      },
      { from, to },
    );
  }

  async replace(
    bucket: StorageBucket,
    oldPath: string,
    file: File,
    userId: string,
    options: UploadOptions,
  ): Promise<StoredFile> {
    return performanceService.measure(
      'storage.replace',
      async () => {
        try {
          await this.provider.delete(bucket, oldPath);
        } catch {
          // Old file may not exist — continue with upload
        }

        const result = await this.upload(file, userId, { ...options, upsert: true });

        await auditService.record({
          actor_id: userId,
          actor_role: 'creator',
          target_type: 'file',
          target_id: result.path,
          action: 'profile_update',
          previous_value: { bucket, path: oldPath },
          new_value: { bucket, path: result.path },
          notes: `Replaced file in ${bucket}`,
        });

        return result;
      },
      { bucket, oldPath },
    );
  }

  async exists(bucket: StorageBucket, path: string): Promise<boolean> {
    return performanceService.measure(
      'storage.exists',
      () => this.provider.exists(bucket, path),
      { bucket, path },
    );
  }

  async metadata(bucket: StorageBucket, path: string): Promise<FileMetadata | null> {
    return performanceService.measure(
      'storage.metadata',
      () => this.provider.metadata(bucket, path),
      { bucket, path },
    );
  }

  async signedUrl(
    bucket: StorageBucket,
    path: string,
    options?: SignedUrlOptions,
  ): Promise<string> {
    return performanceService.measure(
      'storage.signedUrl',
      () => this.provider.downloadUrl(bucket, path, { ...options, expiresIn: options?.expiresIn ?? 3600 }),
      { bucket, path },
    );
  }

  async list(bucket: StorageBucket, options?: ListOptions): Promise<ListResult> {
    return performanceService.measure(
      'storage.list',
      () => this.provider.list(bucket, options),
      { bucket },
    );
  }
}

export const storageService = new StorageServiceImpl();
